/**
 * LiveKit Connection Manager
 * Handles room connection, text streams for chat, and audio tracks for voice.
 *
 * Chat messages: sent via sendText() with topic 'message' (matches agent's registerTextStreamHandler)
 * Agent responses: received via registerTextStreamHandler for topics 'client_transcription' and 'message'
 * Voice: mic published via setMicrophoneEnabled(), agent audio via TrackSubscribed
 */

// Static import — Vite bundles this directly into both UMD and ES builds.
// The previous dynamic import('livekit-client') was replaced by an empty module
// stub in UMD builds, causing livekit-client to never load.
import * as LiveKitClientModule from 'livekit-client';
import { logger } from './logger.js';

let Room, RoomEvent;

async function loadLiveKitSDK() {
    if (Room) return;

    // Primary: use the statically imported module
    // Fallback: window.LivekitClient for legacy 2-script CDN embed
    const lk = (LiveKitClientModule && LiveKitClientModule.Room)
        ? LiveKitClientModule
        : (typeof window !== 'undefined' && window.LivekitClient)
            ? window.LivekitClient
            : null;

    if (!lk || !lk.Room) {
        throw new Error('[iGreet] livekit-client failed to load');
    }

    Room = lk.Room;
    RoomEvent = lk.RoomEvent;
}

export class LiveKitConnection {
    /**
     * @param {Object} opts
     * @param {string} opts.token
     * @param {string} opts.url
     * @param {Function} opts.onMessage — called with { role, content, time }
     * @param {Function} opts.onStateChange — called with room connection state
     */
    constructor({ token, url, onMessage, onStateChange, onTyping }) {
        this.token = token;
        this.url = url;
        this.onMessage = onMessage;
        this.onStateChange = onStateChange;
        this.onTyping = onTyping;
        this.room = null;
        this._audioElements = [];  // Track attached agent audio elements
        this._isAgentMuted = true; // Start muted — widget controls when to unmute
        this._identity = null;
        this._metadata = null;
        this.isAgentTakenOver = false; // True when human-agent is in the room
    }

    async connect() {
        await loadLiveKitSDK();

        this.room = new Room({
            adaptiveStream: true,
            dynacast: true,
        });

        // ── Event: incoming audio (agent TTS or human-agent voice) ──
        this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
            if (track.kind === 'audio') {
                const audioEl = track.attach();
                audioEl.style.display = 'none';
                audioEl.dataset.livekitAgent = 'true';
                // Tag with participant identity so we can selectively mute
                audioEl.dataset.participantIdentity = participant?.identity || 'unknown';
                const isHumanAgent = participant?.identity?.startsWith('human-agent');
                // Human agent audio always plays; bot audio follows mute state
                audioEl.muted = isHumanAgent ? false : this._isAgentMuted;
                document.body.appendChild(audioEl);
                this._audioElements.push(audioEl);
                logger.info('[iGreet] Audio track attached from:', participant?.identity, 'muted:', audioEl.muted, 'isHumanAgent:', isHumanAgent);
            }
        });

        // ── Event: track unsubscribed — cleanup ──
        this.room.on(RoomEvent.TrackUnsubscribed, (track) => {
            if (track.kind === 'audio') {
                const detached = track.detach();
                detached.forEach(el => el.remove());
                this._audioElements = this._audioElements.filter(
                    el => !detached.includes(el)
                );
            }
        });

        // ── Event: connection state ──
        this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
            this.onStateChange?.(state);
        });

        // ── Event: participant joined — detect human agent takeover ──
        this.room.on(RoomEvent.ParticipantConnected, (participant) => {
            if (participant.identity?.startsWith('human-agent')) {
                logger.info('[iGreet] 🤝 Human agent joined — takeover active:', participant.identity);
                this.isAgentTakenOver = true;
                // Mute only BOT audio — human agent audio should play through
                this._muteBotAudioOnly();
            }
        });

        // ── Event: participant left — detect human agent leaving ──
        this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
            if (participant.identity?.startsWith('human-agent')) {
                logger.info('[iGreet] 🤝 Human agent left — takeover ended:', participant.identity);
                this.isAgentTakenOver = false;
                // Restore audio based on current mode (widget.js will handle unmuting if in voice)
            }
        });

        // ── Connect to room ──
        await this.room.connect(this.url, this.token);

        // Store identity for message payloads
        this._identity = this.room.localParticipant?.identity;
        try {
            this._metadata = JSON.parse(this.room.localParticipant?.metadata || '{}');
        } catch {
            this._metadata = {};
        }

        // ── Register text stream handlers (AFTER connection) ──
        // Agent sends transcriptions (bot responses) via topic 'client_transcription'
        this.room.registerTextStreamHandler('client_transcription', async (reader) => {
            try {
                const text = await reader.readAll();
                const data = JSON.parse(text);
                logger.info('[iGreet] Agent transcription:', data);

                // Suppress BOT transcriptions during agent takeover
                // But ALLOW messages from the human agent (identity starts with 'human-agent')
                const isFromHumanAgent = data.identity?.startsWith?.('human-agent');
                if (this.isAgentTakenOver && data.role !== 'user' && !isFromHumanAgent) {
                    logger.info('[iGreet] 🤝 Suppressing bot transcription — agent is handling');
                    return;
                }

                if (data.isFinal && data.text) {
                    // Use role from transcription data — 'user' for speech-to-text, 'assistant' for bot
                    const role = data.role === 'user' ? 'user' : 'assistant';
                    this.onMessage?.({
                        role,
                        content: data.text,
                        time: new Date(),
                    });
                }
            } catch (err) {
                logger.error('[iGreet] Failed to parse transcription stream:', err);
            }
        });

        // Agent can also send chat via topic 'message'
        this.room.registerTextStreamHandler('message', async (reader, participantInfo) => {
            try {
                const text = await reader.readAll();
                const data = JSON.parse(text);
                logger.info('[iGreet] Message from:', participantInfo?.identity, data);
                // Only show messages from agent, not our own echoes
                if (data.role !== 'user' && data.text) {
                    this.onMessage?.({
                        role: 'assistant',
                        content: data.text,
                        time: new Date(),
                    });
                }
            } catch (err) {
                logger.error('[iGreet] Failed to parse message stream:', err);
            }
        });

        // System messages (typing indicators, etc.)
        this.room.registerTextStreamHandler('system', async (reader) => {
            try {
                const text = await reader.readAll();
                const data = JSON.parse(text);
                if (data.type === 'typing') {
                    this.onTyping?.({ identity: data.identity });
                }
            } catch (err) {
                // Ignore malformed system messages
            }
        });

        logger.info('[iGreet] Text stream handlers registered');
    }

    /**
     * Send a chat message via text stream with topic 'message'
     * Matches agent's registerTextStreamHandler('message', handler)
     */
    async sendChatMessage(text) {
        if (!this.room) return;
        const payload = {
            type: 'chat',
            text,
            role: 'user',
            identity: this._identity || 'visitor',
            sid: this._metadata?.sid || '',
        };
        await this.room.localParticipant.sendText(
            JSON.stringify(payload),
            { topic: 'message' }
        );
        logger.info('[iGreet] Chat message sent via text stream:', text);
    }

    async sendText(text) {
        return this.sendChatMessage(text);
    }

    /**
     * Enable microphone for voice mode
     */
    async enableMicrophone() {
        if (!this.room) return;
        await this.room.localParticipant.setMicrophoneEnabled(true);
        logger.info('[iGreet] Microphone enabled');
    }

    /**
     * Disable microphone (switch back to text mode)
     */
    async disableMicrophone() {
        if (!this.room) return;
        await this.room.localParticipant.setMicrophoneEnabled(false);
        logger.info('[iGreet] Microphone disabled');
    }

    /**
     * Check if microphone is currently enabled
     */
    isMicEnabled() {
        if (!this.room) return false;
        return this.room.localParticipant.isMicrophoneEnabled;
    }

    /**
     * Mute all bot audio elements (text mode — no audio from bot)
     * Human agent audio is never muted by this.
     */
    muteAgentAudio() {
        this._isAgentMuted = true;
        this._audioElements.forEach(el => {
            // Don't mute human agent audio
            if (!el.dataset.participantIdentity?.startsWith('human-agent')) {
                el.muted = true;
            }
        });
        logger.info('[iGreet] Bot audio muted (human-agent audio unaffected)');
    }

    /**
     * Unmute all bot audio elements (voice mode — audio enabled)
     * During takeover, only human agent audio plays; bot stays muted.
     */
    unmuteAgentAudio() {
        // During takeover, don't unmute bot — only human agent audio is allowed
        if (this.isAgentTakenOver) {
            logger.info('[iGreet] Bot audio unmute blocked — human agent is handling. Agent audio stays on.');
            return;
        }
        this._isAgentMuted = false;
        this._audioElements.forEach(el => { el.muted = false; });
        logger.info('[iGreet] All audio unmuted');
    }

    /**
     * Mute only bot audio — used during takeover to silence bot but keep human agent audio
     * @private
     */
    _muteBotAudioOnly() {
        this._audioElements.forEach(el => {
            if (!el.dataset.participantIdentity?.startsWith('human-agent')) {
                el.muted = true;
            }
        });
        logger.info('[iGreet] Bot-only audio muted (human agent audio unaffected)');
    }

    /**
     * Clean disconnect
     */
    disconnect() {
        // Remove audio elements
        this._audioElements.forEach(el => el.remove());
        this._audioElements = [];

        if (this.room) {
            this.room.disconnect(true);
            this.room = null;
        }
    }
}
